"use client";

import { useState } from "react";
import Link from "next/link";
import { useTranslations } from "next-intl";
import {
  PLAN_PRICES,
  PLAN_FEATURES,
  formatPlanAmount,
  getAnnualSavings,
  type PlanTier,
} from "@/config/pricing";
import { isPublicSignupOpen } from "@/lib/signup-access";

// While public signup is closed (private preview, default), self-serve plan CTAs
// are replaced with "Request Access" — consistent with the homepage, header, and
// Security page. Public-launch CTA labels return only when public signup is enabled.
const PRIVATE_PREVIEW_CTA = "Request Access";

// ── Billing toggle + price-sensitive plan cards ────────────────
// This is the only client island on the pricing page.
// Everything else (comparison table, FAQ, security, etc.) is server-rendered.

interface PlanDef {
  tier: PlanTier;
  nameKey: string;
  descKey: string;
  ctaKey: string;
  href: string;
  popular: boolean;
}

const PLAN_DEFS: PlanDef[] = [
  { tier: "growth", nameKey: "growthName", descKey: "growthDesc", ctaKey: "growthCta", href: "/signup?plan=growth", popular: false },
  { tier: "business", nameKey: "businessName", descKey: "businessDesc", ctaKey: "businessCta", href: "/signup", popular: true },
  { tier: "agency", nameKey: "agencyName", descKey: "agencyDesc", ctaKey: "agencyCta", href: "/signup", popular: false },
];

const AGENCY_EXPANSION_PRICE = 197;

function CheckIcon({ className = "w-4 h-4 text-green-500 flex-shrink-0" }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );
}

export default function PricingInteractive() {
  const t = useTranslations("pricing");
  const [annual, setAnnual] = useState(false);
  const publicSignup = isPublicSignupOpen();

  return (
    <>
      {/* ── Billing Toggle ─────────────────────────────────────── */}
      <div className="text-center px-6">
      <div className="flex items-center justify-center gap-3 mb-4">
        <span className={`text-sm font-medium ${!annual ? "text-gray-900 dark:text-white" : "text-gray-500"}`}>
          {t("monthly")}
        </span>
        <button
          onClick={() => setAnnual(!annual)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            annual ? "bg-blue-600" : "bg-gray-300 dark:bg-gray-700"
          }`}
          aria-label="Toggle annual pricing"
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              annual ? "translate-x-6" : "translate-x-1"
            }`}
          />
        </button>
        <span className={`text-sm font-medium ${annual ? "text-gray-900 dark:text-white" : "text-gray-500"}`}>
          {t("yearly")}
        </span>
        {annual && (
          <span className="text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-0.5 rounded-full">
            {t("saveMoreAnnual")}
          </span>
        )}
      </div>

      <div className="mt-8">
        <p className="text-gray-500 text-sm">{t("scrollToPlansSubtext")}</p>
      </div>
      </div>{/* close text-center wrapper */}

      {/* ── Free Tier Banner ───────────────────────────────────── */}
      <section className="px-6 py-10">
        <div className="max-w-3xl mx-auto">
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-2 border-green-200 dark:border-green-800 rounded-2xl p-6 md:p-8 flex flex-col md:flex-row items-center gap-6">
            <div className="flex-1 text-center md:text-left">
              <div className="inline-flex items-center gap-2 bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 text-xs font-bold px-3 py-1 rounded-full mb-3">
                <CheckIcon className="w-3.5 h-3.5 text-green-700 dark:text-green-300 flex-shrink-0" />
                {t("noCreditCard")}
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{t("startFreeHeading")}</h2>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                {t("startFreeDesc")}
              </p>
            </div>
            <div className="flex-shrink-0">
              <Link
                href="/signup"
                className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-bold text-lg px-8 py-4 rounded-xl transition shadow-lg shadow-green-500/25"
              >
                {publicSignup ? t("getStartedFree") : PRIVATE_PREVIEW_CTA}
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
              <p className="text-center text-xs text-gray-400 mt-2">{t("freeForever")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Plan Cards (3-column) ──────────────────────────────── */}
      <section id="pricing-cards-section" className="pb-16 px-6 scroll-mt-4">
        <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-6">
          {PLAN_DEFS.map((plan) => {
            const prices = PLAN_PRICES[plan.tier];
            const price = annual ? prices.annualMonthly : prices.monthly;
            const displayPrice = formatPlanAmount(price);
            const displayPeriod = price === 0 ? t("forever") : "/month";
            const features = PLAN_FEATURES[plan.tier];

            return (
              <div
                key={plan.tier}
                className={`rounded-xl p-8 border-2 ${
                  plan.popular
                    ? "border-blue-600 bg-blue-50/50 dark:bg-blue-900/20 relative"
                    : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-xs font-medium px-3 py-1 rounded-full">
                    {t("mostPopular")}
                  </div>
                )}
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {t(plan.nameKey)}
                </h3>
                <div className="flex items-baseline gap-1 mt-3 mb-1">
                  <span className="text-4xl font-bold text-gray-900 dark:text-white">
                    {displayPrice}
                  </span>
                  <span className="text-gray-500">{displayPeriod}</span>
                </div>
                {annual && price > 0 && (
                  <p className="text-xs text-green-600 dark:text-green-400 mb-1">
                    {formatPlanAmount(prices.annualMonthly * 12)}/year (save {formatPlanAmount(getAnnualSavings(plan.tier))}/year)
                  </p>
                )}
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  {t(plan.descKey)}
                </p>
                <ul className="space-y-3 mb-8">
                  {features.map((f, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <CheckIcon />
                      {f}
                    </li>
                  ))}
                </ul>
                <Link
                  href={plan.href}
                  className={`block text-center py-2.5 rounded-lg font-medium text-sm transition ${
                    plan.popular
                      ? "bg-blue-600 hover:bg-blue-700 text-white"
                      : "bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-900 dark:text-white"
                  }`}
                >
                  {publicSignup ? t(plan.ctaKey) : PRIVATE_PREVIEW_CTA}
                </Link>
              </div>
            );
          })}
        </div>

        {/* ── Agency Elite Card ───────────────────────────────── */}
        <div className="max-w-6xl mx-auto mt-10">
          <div className="relative bg-gradient-to-br from-gray-900 to-gray-800 dark:from-gray-800 dark:to-gray-700 rounded-2xl p-8 md:p-10 text-white overflow-hidden">
            <div className="absolute top-0 right-0 w-80 h-80 bg-gradient-to-br from-purple-500/10 to-blue-500/10 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="relative z-10 flex flex-col md:flex-row gap-8">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-4">
                  <div className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-500 to-blue-500 text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                    {t("agencyEliteBadge")}
                  </div>
                </div>
                <h3 className="text-2xl md:text-3xl font-bold mb-2">
                  {t("agencyEliteName")}
                </h3>
                <div className="flex items-baseline gap-2 mb-1">
                  {annual && (
                    <span className="text-lg text-gray-400 line-through">
                      {formatPlanAmount(PLAN_PRICES.agency_elite.monthly)}
                    </span>
                  )}
                  <span className="text-4xl font-bold text-white">
                    {formatPlanAmount(annual ? PLAN_PRICES.agency_elite.annualMonthly : PLAN_PRICES.agency_elite.monthly)}
                  </span>
                  <span className="text-gray-400">/month</span>
                </div>
                {annual && (
                  <p className="text-xs text-green-400 mb-3">
                    Save {formatPlanAmount(getAnnualSavings("agency_elite"))}/year with annual billing
                  </p>
                )}
                <p className="text-gray-300 mb-4">{t("agencyEliteDesc")}</p>

                <div className="bg-white/10 border border-white/20 rounded-xl p-4 mb-4">
                  <p className="text-sm font-semibold text-white mb-1">
                    {t("wholesaleExpansion")}
                  </p>
                  <p className="text-sm text-gray-300">
                    {t("wholesaleDetail")}
                  </p>
                  <p className="text-xs text-purple-300 mt-1 font-medium">
                    {t("costPerAccountDrops", { perUnit: t("wholesalePerUnit") })}
                  </p>
                </div>
              </div>

              <div className="md:w-80 flex-shrink-0">
                <ul className="space-y-2.5 mb-6">
                  {PLAN_FEATURES.agency_elite.map((f, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm text-gray-200">
                      <CheckIcon className="w-4 h-4 text-green-400 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Link
                  href="/signup"
                  className="block text-center bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white font-semibold py-3 rounded-xl transition shadow-lg shadow-purple-500/25"
                >
                  {publicSignup ? t("startFree") : PRIVATE_PREVIEW_CTA}
                </Link>
                <p className="text-center text-xs text-gray-400 mt-2">
                  {t("moneyBackGuarantee")}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ── Revenue Ladder ─────────────────────────────────── */}
        <div className="max-w-4xl mx-auto mt-8 bg-purple-50 dark:bg-purple-900/10 border border-purple-200 dark:border-purple-800 rounded-xl p-6">
          <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-3">{t("agencyGrowthPath")}</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div>
              <p className="text-xs text-gray-400 mb-1">{t("launchTier")}</p>
              <p className="text-lg font-bold text-purple-700 dark:text-purple-300">{formatPlanAmount(PLAN_PRICES.agency.monthly / 15)}</p>
              <p className="text-xs text-gray-400">{t("perClient")}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400 mb-1">{t("growthTier")}</p>
              <p className="text-lg font-bold text-purple-700 dark:text-purple-300">{formatPlanAmount((PLAN_PRICES.agency.monthly + AGENCY_EXPANSION_PRICE) / 25)}</p>
              <p className="text-xs text-gray-400">{t("perClient")}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400 mb-1">{t("scaleTier")}</p>
              <p className="text-lg font-bold text-purple-700 dark:text-purple-300">{formatPlanAmount((PLAN_PRICES.agency.monthly + AGENCY_EXPANSION_PRICE * 2) / 35)}</p>
              <p className="text-xs text-gray-400">{t("perClient")}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400 mb-1">{t("eliteTier")}</p>
              <p className="text-lg font-bold text-purple-700 dark:text-purple-300">{formatPlanAmount((PLAN_PRICES.agency.monthly + AGENCY_EXPANSION_PRICE * 3) / 45)}</p>
              <p className="text-xs text-gray-400">{t("perClient")}</p>
            </div>
          </div>
        </div>

        {/* ── Overage Pricing ────────────────────────────────── */}
        <div className="max-w-4xl mx-auto text-center mt-8 mb-4">
          <div className="inline-flex items-center gap-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 text-sm font-medium px-6 py-3 rounded-full">
            <span>{t("needMore")} <strong>{t("extraMessages")}</strong></span>
            <span className="w-1 h-1 bg-blue-400 rounded-full"></span>
            <span><strong>{t("extraAgents")}</strong></span>
          </div>
        </div>

        {/* ── Trust Badges ───────────────────────────────────── */}
        <div className="max-w-4xl mx-auto text-center mt-4">
          <div className="inline-flex items-center gap-2 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 text-sm font-medium px-4 py-2 rounded-full">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            {t("moneyBackText")}
          </div>
          <div className="text-center mt-4">
            <SwitchoverBadge />
            <p className="text-xs text-gray-400 mt-2">
              {t("switchoverNote")}
            </p>
          </div>
        </div>
      </section>

      {/* ── Enterprise Prices (toggle-dependent) ───────────────── */}
      <section className="py-16 px-6 bg-gradient-to-br from-gray-900 via-blue-950 to-purple-950">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-yellow-500/20 text-yellow-300 text-xs font-bold px-3 py-1.5 rounded-full mb-6">
              <span className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse" />
              {t("enterpriseComingSoon")}
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {t("enterpriseBuiltTitle")}
            </h2>
            <p className="text-lg text-blue-200 max-w-2xl mx-auto">
              {t("enterpriseBuiltSubtitle")}
            </p>
          </div>

          <div className="relative bg-gradient-to-br from-orange-900/40 to-yellow-900/40 border border-yellow-500/30 rounded-2xl p-8 md:p-10 text-white overflow-hidden">
            <div className="absolute top-0 right-0 w-80 h-80 bg-gradient-to-br from-yellow-500/5 to-orange-500/5 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="relative z-10 flex flex-col md:flex-row gap-8">
              <div className="flex-1">
                <h3 className="text-2xl md:text-3xl font-bold mb-2">{t("enterpriseName")}</h3>
                <div className="flex items-baseline gap-2 mb-1">
                  {annual && (
                    <span className="text-lg text-gray-400 line-through">{formatPlanAmount(PLAN_PRICES.enterprise.monthly)}</span>
                  )}
                  <span className="text-4xl font-bold text-white">
                    {formatPlanAmount(annual ? PLAN_PRICES.enterprise.annualMonthly : PLAN_PRICES.enterprise.monthly)}
                  </span>
                  <span className="text-gray-400">/month</span>
                </div>
                {annual && (
                  <p className="text-xs text-green-400 mb-3">
                    Save {formatPlanAmount(getAnnualSavings("enterprise"))}/year with annual billing
                  </p>
                )}
                <p className="text-gray-300 mb-4">{t("enterpriseDesc")}</p>
                <p className="text-sm text-yellow-200/80 mb-4">
                  {t("enterpriseCustomVolume")}
                </p>
              </div>
              <div className="md:w-96 flex-shrink-0">
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6">
                  {PLAN_FEATURES.enterprise.map((f, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm text-gray-200">
                      <CheckIcon className="w-4 h-4 text-yellow-400 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href="mailto:hello@adityalabs.ai?subject=Enterprise%20Plan%20—%20Register%20Interest"
                    className="flex-1 text-center bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white font-semibold py-3 rounded-xl transition shadow-lg shadow-yellow-500/25"
                  >
                    {t("registerInterest")}
                  </a>
                  <a
                    href="mailto:hello@adityalabs.ai?subject=Enterprise%20Plan%20Inquiry"
                    className="flex-1 text-center bg-white/10 hover:bg-white/20 text-white font-medium py-3 rounded-xl transition border border-white/20"
                  >
                    {t("contactSales")}
                  </a>
                </div>
                <p className="text-center text-xs text-yellow-300/70 mt-2">{t("enterpriseLaunchingSoon")}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

// Inline the badge to avoid importing a server component into a client one
function SwitchoverBadge() {
  return (
    <div className="inline-flex items-center gap-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-300 rounded-full px-4 py-2">
      <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
      </svg>
      <span className="text-sm font-semibold">Migration planning support</span>
    </div>
  );
}
